/**
 * Client Uploads application.
 *
 * A single authenticated page where a client uploads large video files
 * (up to 2GB) directly to S3 via presigned URLs, and sees a list of their
 * previous uploads with download links.
 */

import type { ChangeEvent } from "react";

import { useCallback, useEffect, useState } from "react";

import type { ClientUpload } from "@/services/uploadService";

import { ProtectedRoute } from "@/components/ProtectedRoute";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import {
	ALLOWED_EXTENSIONS,
	getDownloadUrl,
	listUploads,
	MAX_FILE_SIZE_BYTES,
	uploadVideo,
} from "@/services/uploadService";

type UploadStatus = "complete" | "error" | "idle" | "uploading";

export function App() {
	return (
		<AuthProvider>
			<ProtectedRoute>
				<UploadPage />
			</ProtectedRoute>
		</AuthProvider>
	);
}

/**
 * Format a byte count as a human-readable string.
 */
function formatBytes(bytes: number): string {
	if (bytes === 0) return "0 B";
	const units = ["B", "KB", "MB", "GB"];
	const exponent = Math.min(
		Math.floor(Math.log(bytes) / Math.log(1024)),
		units.length - 1,
	);
	const value = bytes / Math.pow(1024, exponent);
	return `${value.toFixed(value >= 10 || exponent === 0 ? 0 : 1)} ${units[exponent]}`;
}

function UploadPage() {
	const { getAccessToken, logout, user } = useAuth();

	const [selectedFile, setSelectedFile] = useState<File | null>(null);
	const [fileError, setFileError] = useState<null | string>(null);
	const [uploadStatus, setUploadStatus] = useState<UploadStatus>("idle");
	const [uploadProgress, setUploadProgress] = useState(0);
	const [uploadError, setUploadError] = useState<null | string>(null);

	const [uploads, setUploads] = useState<ClientUpload[]>([]);
	const [isLoadingUploads, setIsLoadingUploads] = useState(true);
	const [listError, setListError] = useState<null | string>(null);

	const refreshUploads = useCallback(async () => {
		setIsLoadingUploads(true);
		setListError(null);
		const accessToken = await getAccessToken();
		if (!accessToken) {
			setListError("Not authenticated");
			setIsLoadingUploads(false);
			return;
		}
		const result = await listUploads(accessToken).catch((error: Error) => {
			setListError(error.message);
			return null;
		});
		if (result) setUploads(result);
		setIsLoadingUploads(false);
	}, [getAccessToken]);

	// Load the uploads list on mount. Deferred via queueMicrotask so the
	// synchronous setState inside refreshUploads doesn't run in the effect body.
	useEffect(() => {
		queueMicrotask(() => void refreshUploads());
	}, [refreshUploads]);

	const handleFileSelect = useCallback(
		(event: ChangeEvent<HTMLInputElement>) => {
			const file = event.target.files?.[0] ?? null;
			setUploadError(null);
			setUploadStatus("idle");

			if (!file) {
				setSelectedFile(null);
				return;
			}

			const extension = file.name.toLowerCase().split(".").pop();
			if (!extension || !ALLOWED_EXTENSIONS.includes(extension as never)) {
				setFileError(
					`Unsupported file type. Accepted formats: ${ALLOWED_EXTENSIONS.join(", ")}.`,
				);
				setSelectedFile(null);
				return;
			}

			if (file.size > MAX_FILE_SIZE_BYTES) {
				setFileError("File exceeds the 2GB maximum.");
				setSelectedFile(null);
				return;
			}

			setFileError(null);
			setSelectedFile(file);
		},
		[],
	);

	const handleUpload = useCallback(async () => {
		if (!selectedFile) return;

		setUploadStatus("uploading");
		setUploadProgress(0);
		setUploadError(null);

		const accessToken = await getAccessToken();
		if (!accessToken) {
			setUploadError("Not authenticated");
			setUploadStatus("error");
			return;
		}

		const result = await uploadVideo(
			selectedFile,
			accessToken,
			setUploadProgress,
		).catch((error: Error) => {
			setUploadError(error.message);
			setUploadStatus("error");
			return null;
		});

		if (result === null) return;

		setUploadStatus("complete");
		setSelectedFile(null);
		void refreshUploads();
	}, [selectedFile, getAccessToken, refreshUploads]);

	const handleDownload = useCallback(
		async (key: string) => {
			const accessToken = await getAccessToken();
			if (!accessToken) return;
			const url = await getDownloadUrl(key, accessToken).catch(() => null);
			if (url) window.open(url, "_blank", "noopener,noreferrer");
		},
		[getAccessToken],
	);

	return (
		<div className="mx-auto max-w-3xl space-y-6 p-6">
			{/* Header */}
			<div className="flex items-center justify-between">
				<div>
					<h1 className="text-lg font-semibold text-gray-900">Video Uploads</h1>
					<p className="mt-1 text-sm text-gray-500">
						Upload large video files (up to 2GB) and download them later.
					</p>
				</div>
				<div className="flex items-center gap-3 text-sm text-gray-500">
					{user?.email && <span>{user.email}</span>}
					<button
						className="btn-secondary"
						onClick={() => logout()}
						type="button"
					>
						Sign out
					</button>
				</div>
			</div>

			{/* Upload card */}
			<div className="card">
				<div className="card-body space-y-4">
					<input
						accept={ALLOWED_EXTENSIONS.map((ext) => `.${ext}`).join(",")}
						className="input"
						disabled={uploadStatus === "uploading"}
						onChange={handleFileSelect}
						type="file"
					/>

					{selectedFile && (
						<div className="text-sm text-gray-600">
							{selectedFile.name} ({formatBytes(selectedFile.size)})
						</div>
					)}

					{fileError && (
						<div className="rounded-lg bg-error-50 p-3 text-sm text-error-700">
							{fileError}
						</div>
					)}

					{uploadStatus === "uploading" && (
						<div className="space-y-1">
							<div className="h-2 w-full overflow-hidden rounded-full bg-gray-100">
								<div
									className="h-full bg-primary-600 transition-all"
									style={{ width: `${uploadProgress}%` }}
								/>
							</div>
							<div className="text-xs text-gray-500">
								Uploading… {uploadProgress}%
							</div>
						</div>
					)}

					{uploadStatus === "complete" && (
						<div className="rounded-lg bg-success-50 p-3 text-sm text-success-700">
							Upload complete.
						</div>
					)}

					{uploadStatus === "error" && uploadError && (
						<div className="rounded-lg bg-error-50 p-3 text-sm text-error-700">
							{uploadError}
						</div>
					)}

					<button
						className="btn-primary w-full"
						disabled={!selectedFile || uploadStatus === "uploading"}
						onClick={handleUpload}
						type="button"
					>
						{uploadStatus === "uploading" ? "Uploading…" : "Upload Video"}
					</button>
				</div>
			</div>

			{/* Uploads list */}
			<div className="card">
				<div className="card-header flex items-center justify-between">
					<h2 className="text-sm font-semibold text-gray-900">Your uploads</h2>
					<button
						className="btn-ghost text-xs"
						disabled={isLoadingUploads}
						onClick={() => void refreshUploads()}
						type="button"
					>
						Refresh
					</button>
				</div>
				<div className="card-body">
					{listError && (
						<div className="rounded-lg bg-error-50 p-3 text-sm text-error-700">
							{listError}
						</div>
					)}

					{!listError && isLoadingUploads && (
						<p className="text-sm text-gray-400">Loading…</p>
					)}

					{!listError && !isLoadingUploads && uploads.length === 0 && (
						<p className="text-sm text-gray-400 italic">No uploads yet.</p>
					)}

					{!listError && !isLoadingUploads && uploads.length > 0 && (
						<ul className="divide-y divide-gray-100">
							{uploads.map((upload) => (
								<li
									className="flex items-center justify-between py-3"
									key={upload.key}
								>
									<div className="min-w-0">
										<div className="truncate text-sm font-medium text-gray-900">
											{upload.filename}
										</div>
										<div className="text-xs text-gray-500">
											{formatBytes(upload.sizeBytes)}
											{upload.lastModified
												? ` · ${new Date(upload.lastModified).toLocaleString()}`
												: ""}
										</div>
									</div>
									<button
										className="btn-secondary text-xs"
										onClick={() => void handleDownload(upload.key)}
										type="button"
									>
										Download
									</button>
								</li>
							))}
						</ul>
					)}
				</div>
			</div>
		</div>
	);
}
